/// <reference types="vite/client" />

/**
 * LPA Wealth — Structured Logger
 * Emite logs estruturados em JSON para facilitar busca/agregação.
 * Em produção, logs vão para console (capturados pelo Vercel/Supabase).
 * Em dev, mantém formato legível.
 *
 * IMPORTANT: All exports use `export function` (not `export const`)
 * because function declarations are HOISTED by the JS engine.
 * This makes them immune to Temporal Dead Zone (TDZ) errors
 * when Rollup reorders declarations in flat production bundles.
 *
 * Uso:
 *   import { logger, logError, logWarn } from "../shared/logger.js";
 *   logger.info("snapshot_imported", { client_id, period });
 *   logger.error("parse_failed", { file, error: err.message });
 *   logger.warn("cache_eviction", { evicted: key });
 *   logError("MyComponent", error, { context: "action_name" });
 *   logWarn("MyComponent", "warning message", { context: "detail" });
 */

import * as Sentry from "@sentry/react";

type LogData = Record<string, any>;
type LogLevel = "debug" | "info" | "warn" | "error";

/** Check dev mode at call time so tests can override import.meta.env.DEV */
function isDev(): boolean {
  return import.meta.env?.DEV ?? false;
}

const LEVELS: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

function getMinLevel(): number {
  return isDev() ? LEVELS.debug : LEVELS.info;
}

function emit(level: LogLevel, event: string, data: LogData = {}): void {
  if (LEVELS[level] < getMinLevel()) return;

  const entry: Record<string, any> = {
    ts: new Date().toISOString(),
    level,
    event,
    app: "lpa-portal",
    ...data,
  };

  // Sanitizar dados sensíveis antes de logar
  if (entry.token) entry.token = "[REDACTED]";
  if (entry.password) entry.password = "[REDACTED]";
  if (entry.cpf) entry.cpf = entry.cpf.replace(/\d{3}\.\d{3}/, "***.**");

  if (isDev()) {
    // Dev: formato legível com cores
    const colors: Record<LogLevel, string> = {
      debug: "\x1b[90m",
      info: "\x1b[36m",
      warn: "\x1b[33m",
      error: "\x1b[31m",
    };
    const reset = "\x1b[0m";
    const prefix = `${colors[level]}[${level.toUpperCase()}]${reset}`;
    const dataStr = Object.keys(data).length > 0 ? ` ${JSON.stringify(data)}` : "";
    const msg = `${prefix} ${event}${dataStr}`;
    if (level === "error") console.error(msg);
    else if (level === "warn") console.warn(msg);
    else console.info(msg);
  } else {
    // Produção: JSON estruturado (capturável por Vercel/Datadog/etc)
    const fn = level === "error" ? console.error : level === "warn" ? console.warn : console.info;
    fn(JSON.stringify(entry));
  }
}

/**
 * logError: Reports errors to both console and Sentry (in production)
 */
export function logError(tag: string, error: unknown, extra: LogData = {}): void {
  const message = error instanceof Error ? error.message : String(error);
  const errorObj = error instanceof Error ? error : new Error(String(error));

  console.error(`[${tag}]`, message, extra);

  if (!isDev()) {
    Sentry.captureException(errorObj, {
      tags: { component: tag },
      extra: { ...extra, tag },
    });
  }
}

/**
 * logWarn: Reports warnings to both console and Sentry (in production)
 */
export function logWarn(tag: string, message: string, extra: LogData = {}): void {
  console.warn(`[${tag}]`, message, extra);

  if (!isDev()) {
    Sentry.captureMessage(message, {
      level: "warning",
      tags: { component: tag },
      extra: { ...extra, tag },
    });
  }
}

/** Logger interface for structured logging */
export interface Logger {
  debug(event: string, data?: LogData): void;
  info(event: string, data?: LogData): void;
  warn(event: string, data?: LogData): void;
  error(event: string, data?: LogData): void;
  time(event: string): { end: (data?: LogData) => number };
}

// Logger object — uses named function expressions inside for debuggability.
// The `logger` const itself is safe: its initializer is an object literal
// (not referencing other module-scoped identifiers at init time).
export const logger: Logger = {
  debug: function logDebug(event: string, data?: LogData): void {
    return emit("debug", event, data);
  },
  info: function logInfo(event: string, data?: LogData): void {
    return emit("info", event, data);
  },
  warn: function logWarnEvt(event: string, data?: LogData): void {
    return emit("warn", event, data);
  },
  error: function logErrorEvt(event: string, data?: LogData): void {
    return emit("error", event, data);
  },

  /** Timer helper: mede duração de operações */
  time: function logTime(event: string) {
    const start = performance.now();
    return {
      end: (data: LogData = {}): number => {
        const ms = Math.round(performance.now() - start);
        emit("info", event, { ...data, duration_ms: ms });
        return ms;
      },
    };
  },
};
